// Oil Paint filter — stylizes image to look like an oil painting
// Uses directional smoothing based on local gradient orientation.

import type { Filter } from "./Filter";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";

export interface OilPaintParams {
  brushSize: number;
  bristleDetail: number;
  scale: number;
}

export class OilPaint implements Filter<OilPaintParams> {
  readonly id = "oil-paint";
  readonly displayName = "Oil Paint";
  readonly defaultParams: OilPaintParams = { brushSize: 3, bristleDetail: 5, scale: 1 };

  apply(src: PixelBuffer, params: OilPaintParams, _selection: Selection | null): PixelBuffer {
    const { width, height } = src;
    const dst = new PixelBuffer(width, height, "rgba8");
    const data = src.data;
    const out = dst.data;
    const radius = Math.max(1, Math.round(params.brushSize));
    const bristle = Math.max(0, Math.min(10, params.bristleDetail));
    const scale = Math.max(0.1, params.scale);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const di = (y * width + x) * 4;
        let gx = 0, gy = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = Math.min(width - 1, Math.max(0, x + dx));
            const ny = Math.min(height - 1, Math.max(0, y + dy));
            const ni = (ny * width + nx) * 4;
            const lum = data[ni]! * 0.299 + data[ni + 1]! * 0.587 + data[ni + 2]! * 0.114;
            gx += dx * lum;
            gy += dy * lum;
          }
        }
        const angle = Math.atan2(gy, gx);
        const dirX = Math.cos(angle) * scale;
        const dirY = Math.sin(angle) * scale;
        let rSum = 0, gSum = 0, bSum = 0, aSum = 0, count = 0;
        for (let s = -radius; s <= radius; s++) {
          for (let t = -radius; t <= radius; t++) {
            const perpX = -dirY;
            const perpY = dirX;
            const sx = Math.round(x + s * dirX + t * perpX * (bristle / 10));
            const sy = Math.round(y + s * dirY + t * perpY * (bristle / 10));
            if (sx >= 0 && sx < width && sy >= 0 && sy < height) {
              const si = (sy * width + sx) * 4;
              rSum += data[si]!;
              gSum += data[si + 1]!;
              bSum += data[si + 2]!;
              aSum += data[si + 3]!;
              count++;
            }
          }
        }
        if (count > 0) {
          out[di] = Math.round(rSum / count);
          out[di + 1] = Math.round(gSum / count);
          out[di + 2] = Math.round(bSum / count);
          out[di + 3] = Math.round(aSum / count);
        } else {
          out[di] = data[di]!;
          out[di + 1] = data[di + 1]!;
          out[di + 2] = data[di + 2]!;
          out[di + 3] = data[di + 3]!;
        }
      }
    }
    dst.markDirty();
    return dst;
  }
}
